import { MATCH_MAX_DIST } from "../useCableDestinationInfo";
import type {
  SummariseUnrouted,
  UnroutedDevice,
  UnroutedElementCentre,
  UnroutedKind,
  UnroutedReason,
} from "./types";

// A missing schedule leads, because it is the only one of the three he can act
// on outside this app — the rest are corrections he makes here.
const REASON_ORDER: UnroutedReason[] = [
  "no_circuit_id_reaches_it",
  // Second, and ahead of the run's own two: a device the estimate does not know
  // exists carries no cable and no price, and it is the one on this list he can
  // settle outright rather than reason about.
  "not_in_the_estimate",
  "every_circuit_vetoed",
  "every_feasible_circuit_full",
  "unknown",
];

const nameOf = (
  device: UnroutedDevice,
  elements: UnroutedElementCentre[],
): UnroutedElementCentre | null => {
  const position = device.position;
  if (!position) return null;
  let best: UnroutedElementCentre | null = null;
  let bestD2 = MATCH_MAX_DIST * MATCH_MAX_DIST;
  for (const element of elements) {
    const dx = element.x - position.x;
    const dy = element.y - position.y;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = element;
    }
  }
  return best;
};

export const summariseUnrouted: SummariseUnrouted = ({
  devices,
  elements = [],
  adoptedElementIds,
}) => {
  const counts = new Map<UnroutedReason, number>();
  const kinds = new Map<string, UnroutedKind>();

  // Filtered once, at the top: `total`, `byReason` and `byKind` all derive from
  // this list, so anything reading them is correct without knowing adoption
  // exists at all.
  const open = adoptedElementIds?.size
    ? devices.filter((device) => !adoptedElementIds.has(device.id))
    : devices;

  for (const device of open) {
    counts.set(device.reason, (counts.get(device.reason) ?? 0) + 1);

    const matched = nameOf(device, elements);
    const name = matched?.groupId || device.name;
    if (!name) continue;
    const kind = kinds.get(name);
    if (kind) {
      kind.count += 1;
      kind.devices.push({
        id: device.id,
        name: device.name,
        number: kind.count,
        position: device.position,
      });
      // The first match wins the icon, but a later one fills a gap the first left.
      if (!kind.iconDataUrl && matched?.iconDataUrl) {
        kind.iconDataUrl = matched.iconDataUrl;
      }
      continue;
    }
    kinds.set(name, {
      name,
      iconDataUrl: matched?.iconDataUrl,
      count: 1,
      devices: [
        {
          id: device.id,
          name: device.name,
          number: 1,
          position: device.position,
        },
      ],
    });
  }

  return {
    total: open.length,
    open,
    byReason: REASON_ORDER.filter((reason) => counts.has(reason)).map(
      (reason) => ({ reason, count: counts.get(reason) as number }),
    ),
    // Biggest kind first — "23 double sockets" is the line he is looking for.
    // The name breaks every tie, so the tiles cannot reshuffle between renders.
    byKind: [...kinds.values()].sort(
      (a, b) => b.count - a.count || a.name.localeCompare(b.name),
    ),
    hasMissingSchedule: counts.has("no_circuit_id_reaches_it"),
  };
};
